import { z } from 'zod';

export const roleSchema = z.enum([
    'superuser',
    'read_only_superuser',
    'owner',
    'admin',
    'content_reader',
    'content_reviewer',
    'content_writer',
    'mobile_user',
    'mobile_publisher',
]);

export const applicationFeatureConfigSchema = z.object({
    enabled: z.boolean(),
});

export const applicationFeaturesSchema = z.object({
    templates: applicationFeatureConfigSchema,
    audios: applicationFeatureConfigSchema,
    segmentIO: applicationFeatureConfigSchema,
    webCreation: applicationFeatureConfigSchema,
    captions: applicationFeatureConfigSchema,
    approvals: applicationFeatureConfigSchema,
    idleSessionTimeout: applicationFeatureConfigSchema,
    secureSession: applicationFeatureConfigSchema,
    customerTeamCode: applicationFeatureConfigSchema,
    collections: applicationFeatureConfigSchema,
    animatedFilters: applicationFeatureConfigSchema,
    shortcuts: applicationFeatureConfigSchema,
});

export const assetsInfoSchema = z.object({
    filters: z.number(),
    fonts: z.number(),
    stickers: z.number(),
    colors: z.number(),
    backgrounds: z.number(),
    templates: z.number(),
    audios: z.number(),
    introOutros: z.number(),
});

export const authMethodTypeSchema = z.enum(['regular', 'sso']);

export const authMethodSchema = z.object({
    method: authMethodTypeSchema,
    value: z.string().optional(),
});

export const applicationConfigurationStatusSchema = z.enum(['active', 'locked', 'archived', 'ok']);

const accountLifecycleSchema = z.enum(['trial', 'paid', 'trial_expired', 'canceled', 'suspended', 'unclassified']);

export const versionValidationResultSchema = z
    .object({
        valid: z.boolean().optional(),
        errors: z.array(z.string()).optional(),
        warnings: z.array(z.string()).optional(),
        status: z.string().optional(),
        validatedAt: z.number().optional(),
    })
    .passthrough();

export const applicationConfigurationSchema = z.object({
    identifier: z.string(),
    id: z.string(),
    name: z.string(),
    api: z.string(),
    status: applicationConfigurationStatusSchema,
    locked: z.boolean(),
    lockReason: z.string().optional(),
    roles: z.array(roleSchema),
    createdAt: z.number().optional(),
    createdBy: z.string().optional(),
    splashURL: z.string().optional(),
    avatarURL: z.string().optional(),
    bannerURL: z.string().optional(),
    authMethods: z.array(authMethodSchema).optional(),
    dangling: z.boolean().optional(),
    latestVersionCreatedAt: z.number().optional(),
    latestVersionDataVersion: z.number().optional(),
    latestVersionMigrationFailedAt: z.number().optional(),
    lastPublishedVersionId: z.number().optional(),
    lastPublishedVersionValidationResult: versionValidationResultSchema.optional(),
    features: applicationFeaturesSchema,
    membersLimit: z.number().optional(),
    billableAssetsLimit: z.number().optional(),
    billableAssetsCount: z.number().optional(),
    assetsInfo: assetsInfoSchema.optional(),
    membersCount: z.number().optional(),
    invitationsCount: z.number().optional(),
    teamCodeSet: z.boolean(),
    adminId: z.string().optional(),
    trialStart: z.string().optional(),
    trialEnd: z.string().optional(),
    accountLifecycle: accountLifecycleSchema.optional(),
});

export type ApplicationConfiguration = z.infer<typeof applicationConfigurationSchema>;
export type ApplicationConfigurationStatus = z.infer<typeof applicationConfigurationStatusSchema>;
export type Role = z.infer<typeof roleSchema>;
export type ApplicationFeatures = z.infer<typeof applicationFeaturesSchema>;
export type AssetsInfo = z.infer<typeof assetsInfoSchema>;
export type AuthMethod = z.infer<typeof authMethodSchema>;
export type AuthMethodType = z.infer<typeof authMethodTypeSchema>;
export type VersionValidationResult = z.infer<typeof versionValidationResultSchema>;
